/**
 * Pure helpers for the campaign form's "Where affiliates send players" picker: the places a campaign's landing URL
 * can point at (the brand's homepage, its live landing pages) and which of them a stored URL is. No React / axios / window here, so it unit-tests in isolation.
 *
 * The picker only fills the same `landing_url` string the owner used to type: the stored value is always the bare
 * page URL, never a Studio tracking link (that one already carries the owner's own `aff_id`).
 */
import type { LandingDestination } from '@/nova/pages/creatives/hooks/useLandingDestinations';
import { sameDestination } from '@/nova/pages/creatives/tracking-link';

/** One place a campaign can send players. `name` is data (a page's own name, '' when it has none). */
export interface DestinationOption {
  key: string;
  kind: 'website' | 'landing';
  name: string;
  url: string;
  imageUrl?: string;
}

/** Which row the picker shows as chosen for the form's current landing URL. */
export type DestinationSelection =
  | { kind: 'option'; key: string }
  | { kind: 'custom' }
  | { kind: 'none' }
  /** The URL may be one of the options still being read: nothing is shown as chosen until they land. */
  | { kind: 'pending' };

/** The brand homepage as an option ('' origin: none). Pure. */
export const websiteOption = (origin: string): DestinationOption | null =>
  origin ? { key: 'website', kind: 'website', name: '', url: origin } : null;

/** The brand's live landing pages as options (the hook already keeps only pages with an address). Pure. */
export const landingOptions = (destinations: readonly LandingDestination[]): DestinationOption[] =>
  destinations.map((d) => ({
    key: `landing:${d.landingPageId ?? d.url}`,
    kind: 'landing',
    name: d.name,
    url: d.url,
    ...(d.imageUrl ? { imageUrl: d.imageUrl } : {}),
  }));

/**
 * The chosen row for a landing URL: the owner's explicit "Custom URL" wins; else the first option at that address
 * (the homepage is listed first, so it wins a tie); else, while some options are still being read, nothing yet;
 * else a non-empty URL is a custom one. Pure.
 */
export const selectionFor = (
  value: string,
  customChosen: boolean,
  options: readonly DestinationOption[],
  optionsLoading: boolean,
): DestinationSelection => {
  if (customChosen) return { kind: 'custom' };
  const url = value.trim();
  if (!url) return { kind: 'none' };
  const hit = options.find((o) => sameDestination(o.url, url));
  if (hit) return { kind: 'option', key: hit.key };
  return optionsLoading ? { kind: 'pending' } : { kind: 'custom' };
};

/** Today as `YYYY-MM-DD` in the owner's own calendar (what a date input shows). Pure given `now`. */
export const localYmd = (now: Date): string => {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

/** An end date before the start date (both `YYYY-MM-DD`, so they compare as strings); blank never conflicts. Pure. */
export const endBeforeStart = (start: string, end: string): boolean => !!start.trim() && !!end.trim() && end < start;
